import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { StatusBadge } from "../components/StatusBadge";
import { toast } from "sonner";
import { Search, Phone, MessageCircle, ArrowRightCircle, Bell, User as UserIcon } from "lucide-react";
import { formatDate, formatApiError } from "../lib/format";
import { INQUIRY_STATUSES, INQUIRY_STATUS_LABEL, INQUIRY_SOURCES, divisionById } from "../lib/constants";
import { waLink } from "../lib/wa";

const STATUS_LABEL_UI = {
  New: "Baru Masuk",
  "Follow-up": "Perlu Follow Up",
  Qualified: "Sudah Dihubungi",
  Quotation: "Penawaran",
  Negotiation: "Negosiasi",
  Accepted: "Deal",
  Lost: "Belum Deal",
  Converted: "Booking",
};

export default function InquiryPool() {
  const nav = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fSource, setFSource] = useState("all");
  const [fStatus, setFStatus] = useState("all");
  const [fLayanan, setFLayanan] = useState("all");
  const [q, setQ] = useState("");
  const [detail, setDetail] = useState(null);
  const [fu, setFu] = useState({ note: "", channel: "WhatsApp", new_status: "", next_followup_at: "" });
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/inquiries").then(({ data }) => { setRows(data); setLoading(false); })
      .catch(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => rows.filter((r) => {
    if (fSource !== "all" && r.source !== fSource) return false;
    if (fStatus !== "all" && r.status !== fStatus) return false;
    if (fLayanan !== "all" && r.division_id !== fLayanan) return false;
    if (q) {
      const s = q.toLowerCase();
      const client = `${r.bride_name || ""} ${r.groom_name || ""} ${r.inquiry_code || ""} ${r.whatsapp || ""}`.toLowerCase();
      if (!client.includes(s)) return false;
    }
    return true;
  }), [rows, fSource, fStatus, fLayanan, q]);

  const kpi = useMemo(() => ({
    baru: rows.filter((r) => r.status === "New").length,
    fu: rows.filter((r) => r.status === "Follow-up").length,
    deal: rows.filter((r) => r.status === "Accepted").length,
    lost: rows.filter((r) => r.status === "Lost").length,
  }), [rows]);

  const openDetail = (r) => {
    setDetail(r);
    setFu({ note: "", channel: "WhatsApp", new_status: "", next_followup_at: "" });
  };

  const submitFollowup = async () => {
    if (!fu.note.trim()) { toast.error("Catatan follow up wajib diisi"); return; }
    setSaving(true);
    try {
      const payload = { note: fu.note, channel: fu.channel };
      if (fu.new_status) payload.new_status = fu.new_status;
      if (fu.next_followup_at) payload.next_followup_at = fu.next_followup_at;
      await api.post(`/inquiries/${detail.id}/followup`, payload);
      toast.success("Follow up tercatat");
      setDetail(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const convertToBooking = async (id) => {
    if (!window.confirm("Ubah inquiry ini menjadi booking?")) return;
    try {
      const { data } = await api.post(`/inquiries/${id}/convert`);
      toast.success(`Booking ${data.booking_code || ""} dibuat`);
      nav(`/bookings/${data.id}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6" data-testid="inquiry-pool-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-2xl font-bold text-[#1E2022]">Inquiry Pool</h1>
          <p className="text-sm text-[#5C6066] mt-1">Setiap form konsultasi dari landing page langsung tercatat di sini secara real-time.</p>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { label: "Baru Masuk", val: kpi.baru, tone: "bg-sky-50 text-sky-700 border-sky-200", testId: "kpi-baru" },
          { label: "Perlu Follow Up", val: kpi.fu, tone: "bg-amber-50 text-amber-800 border-amber-200", testId: "kpi-fu" },
          { label: "Deal", val: kpi.deal, tone: "bg-emerald-50 text-emerald-800 border-emerald-200", testId: "kpi-deal" },
          { label: "Belum Deal", val: kpi.lost, tone: "bg-rose-50 text-rose-700 border-rose-200", testId: "kpi-lost" },
        ].map((k) => (
          <Card key={k.label} className={`p-4 border ${k.tone} shadow-none`} data-testid={k.testId}>
            <div className="text-[10px] uppercase tracking-wider font-semibold">{k.label}</div>
            <div className="mt-1 font-display text-2xl font-bold">{k.val}</div>
          </Card>
        ))}
      </div>

      <Card className="border border-[#E5E0D8] bg-white shadow-none">
        <div className="p-4 border-b border-[#E5E0D8] flex items-center gap-2 flex-wrap">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-[#8C9096]" />
            <Input className="pl-9 h-9" placeholder="Cari nama pasangan, nomor WA, kode..." value={q} onChange={(e) => setQ(e.target.value)} data-testid="pool-search" />
          </div>
          <Select value={fSource} onValueChange={setFSource}>
            <SelectTrigger className="w-40 h-9" data-testid="pool-filter-source"><SelectValue placeholder="Sumber" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Semua Sumber</SelectItem>
              {INQUIRY_SOURCES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={fStatus} onValueChange={setFStatus}>
            <SelectTrigger className="w-40 h-9" data-testid="pool-filter-status"><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Semua Status</SelectItem>
              {INQUIRY_STATUSES.map((s) => <SelectItem key={s} value={s}>{STATUS_LABEL_UI[s] || INQUIRY_STATUS_LABEL[s] || s}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={fLayanan} onValueChange={setFLayanan}>
            <SelectTrigger className="w-44 h-9" data-testid="pool-filter-layanan"><SelectValue placeholder="Layanan" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Semua Layanan</SelectItem>
              {["div-mua","div-decor","div-photo","div-attire","div-music","div-wo"].map((d) => (
                <SelectItem key={d} value={d}>{divisionById(d)?.short || d}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#FAF6EE] text-[#5C6066]">
              <tr className="text-left">
                <th className="px-4 py-2 font-semibold">Inquiry ID</th>
                <th className="px-4 py-2 font-semibold">Nama Pasangan</th>
                <th className="px-4 py-2 font-semibold">Sumber</th>
                <th className="px-4 py-2 font-semibold">Tanggal Acara</th>
                <th className="px-4 py-2 font-semibold">Layanan</th>
                <th className="px-4 py-2 font-semibold">Status</th>
                <th className="px-4 py-2 font-semibold">PIC Follow Up</th>
                <th className="px-4 py-2 font-semibold text-right">Aksi</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#F0EBE0]">
              {loading && <tr><td colSpan={8} className="text-center py-10 text-[#8C9096]">Memuat…</td></tr>}
              {!loading && filtered.length === 0 && <tr><td colSpan={8} className="text-center py-10 text-[#8C9096]">Tidak ada inquiry.</td></tr>}
              {filtered.map((r) => (
                <tr key={r.id} className="hover:bg-[#FAF6EE]/40" data-testid={`pool-row-${r.inquiry_code}`}>
                  <td className="px-4 py-3 font-mono text-xs">{r.inquiry_code}</td>
                  <td className="px-4 py-3 font-medium">{`${r.bride_name || ""} & ${r.groom_name || ""}`.trim(" &")}</td>
                  <td className="px-4 py-3"><span className="inline-flex px-2 py-0.5 rounded-md bg-[#F0EBE0] text-[#5C6066] text-[11px] font-semibold">{r.source || "-"}</span></td>
                  <td className="px-4 py-3 text-[#5C6066]">{r.event_date ? formatDate(r.event_date) : "-"}</td>
                  <td className="px-4 py-3 text-[11px]">{r.division_id ? (divisionById(r.division_id)?.short || r.division_id) : (r.package_interest || "-")}</td>
                  <td className="px-4 py-3"><StatusBadge status={r.status} label={STATUS_LABEL_UI[r.status] || r.status} /></td>
                  <td className="px-4 py-3 text-xs text-[#5C6066]">{r.last_followup_by || r.assigned_name || "—"}</td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <Button size="sm" variant="outline" className="h-8 mr-1" onClick={() => openDetail(r)} data-testid={`pool-open-${r.inquiry_code}`}>Follow Up</Button>
                    {r.whatsapp && <Button size="sm" variant="outline" className="h-8 mr-1" onClick={() => window.open(waLink(r.whatsapp), "_blank")} title="WhatsApp"><MessageCircle className="w-3.5 h-3.5"/></Button>}
                    {r.status !== "Converted" && <Button size="sm" className="h-8 bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => convertToBooking(r.id)} data-testid={`pool-convert-${r.inquiry_code}`}><ArrowRightCircle className="w-3.5 h-3.5"/></Button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-2xl" data-testid="pool-detail-modal">
          <DialogHeader>
            <DialogTitle>Detail Inquiry {detail?.inquiry_code}</DialogTitle>
          </DialogHeader>
          {detail && (
            <div className="space-y-4 text-sm">
              <div className="grid grid-cols-2 gap-3 p-3 bg-[#FAF6EE] rounded-md border border-[#F0EBE0]">
                <div><Label className="text-xs">Nama</Label><div className="font-medium">{`${detail.bride_name || ""} & ${detail.groom_name || ""}`.trim(" &")}</div></div>
                <div><Label className="text-xs">WhatsApp</Label><div className="font-medium">{detail.whatsapp || "-"}</div></div>
                <div><Label className="text-xs">Tanggal Acara</Label><div>{detail.event_date ? formatDate(detail.event_date) : "-"}</div></div>
                <div><Label className="text-xs">Venue / Kota</Label><div>{detail.venue || detail.location || "-"}</div></div>
                <div><Label className="text-xs">Budget</Label><div>{detail.budget_range || "-"}</div></div>
                <div><Label className="text-xs">Sumber</Label><div>{detail.source || "-"}</div></div>
              </div>

              <div>
                <Label className="text-xs">Timeline Aktivitas</Label>
                <div className="mt-2 max-h-40 overflow-y-auto space-y-1.5 border border-[#E5E0D8] rounded-md p-2">
                  {(detail.status_history || []).slice().reverse().map((h, i) => (
                    <div key={i} className="text-xs flex items-start gap-2">
                      <Bell className="w-3 h-3 mt-0.5 text-[#B7A175] shrink-0"/>
                      <div className="flex-1">
                        <div><b>{STATUS_LABEL_UI[h.status] || h.status}</b> · <span className="text-[#8C9096]">{new Date(h.at).toLocaleString("id-ID")}</span></div>
                        {h.note && <div className="text-[#5C6066]">{h.note}</div>}
                        {(h.by_name || h.by) && <div className="text-[10px] text-[#8C9096]">oleh {h.by_name || h.by} {h.channel ? `· ${h.channel}` : ""}</div>}
                      </div>
                    </div>
                  ))}
                  {(!detail.status_history || detail.status_history.length === 0) && <div className="text-xs text-[#8C9096] text-center py-2">Belum ada aktivitas.</div>}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs" htmlFor="fu-channel">Channel</Label>
                  <Select value={fu.channel} onValueChange={(v) => setFu({ ...fu, channel: v })}>
                    <SelectTrigger className="h-9" id="fu-channel" data-testid="fu-channel"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {["WhatsApp","Call","Email","Meeting"].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs" htmlFor="fu-status">Ubah Status</Label>
                  <Select value={fu.new_status || "keep"} onValueChange={(v) => setFu({ ...fu, new_status: v === "keep" ? "" : v })}>
                    <SelectTrigger className="h-9" id="fu-status" data-testid="fu-status"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="keep">Tidak diubah</SelectItem>
                      {INQUIRY_STATUSES.filter((s) => s !== "Converted").map((s) => <SelectItem key={s} value={s}>{STATUS_LABEL_UI[s] || s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="md:col-span-2">
                  <Label className="text-xs" htmlFor="fu-note">Catatan Follow Up *</Label>
                  <Textarea id="fu-note" rows={3} placeholder="Detail komunikasi..." value={fu.note} onChange={(e) => setFu({ ...fu, note: e.target.value })} data-testid="fu-note" />
                </div>
                <div>
                  <Label className="text-xs" htmlFor="fu-reminder">Reminder Follow Up berikutnya</Label>
                  <Input id="fu-reminder" type="datetime-local" value={fu.next_followup_at} onChange={(e) => setFu({ ...fu, next_followup_at: e.target.value })} data-testid="fu-reminder" />
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDetail(null)}>Batal</Button>
            <Button onClick={submitFollowup} disabled={saving} data-testid="fu-submit" className="bg-[#B7A175] hover:bg-[#A08A5D] text-white">Simpan Follow Up</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
